import { Crown, Loader2, Search, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { CompanionLabel } from '@/components/common/CompanionLabel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useGetIntakesQuery,
  useLinkIntakeCompanionsMutation,
} from '@/services/api/intakeService';
import { IntakeListItem } from '@/services/types/intake-types';

interface LinkIntakesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface IntakePickerProps {
  label: string;
  selected: IntakeListItem | null;
  onSelect: (intake: IntakeListItem) => void;
  onClear: () => void;
  excludeId?: string;
}

const IntakePicker: React.FC<IntakePickerProps> = ({
  label,
  selected,
  onSelect,
  onClear,
  excludeId,
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [isOpen, setIsOpen] = useState(false);

  const { data, isFetching } = useGetIntakesQuery(
    { search: debouncedSearch, limit: 10 },
    { skip: !debouncedSearch || debouncedSearch.length < 2 },
  );

  const results = useMemo(
    () =>
      (data?.results || []).filter((item) => item.id !== excludeId),
    [data?.results, excludeId],
  );

  if (selected) {
    return (
      <div className="space-y-1">
        <span className="text-xs font-medium text-zinc-500">{label}</span>
        <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 dark:text-white truncate">
              {selected.client_name}
            </p>
            <p className="text-xs text-zinc-500 truncate">
              {selected.firm_name} &middot; {selected.status}
            </p>
          </div>
          {selected.companion_count > 0 && (
            <CompanionLabel
              memberCount={selected.companion_count}
              isPrimary={selected.is_companion_primary}
              size="sm"
            />
          )}
          <button
            type="button"
            onClick={onClear}
            className="text-zinc-400 hover:text-zinc-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-zinc-500">{label}</span>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-zinc-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search by client name..."
          className="w-full rounded-md border border-zinc-200 bg-white py-2 pl-8 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
        />
        {isOpen && debouncedSearch.length >= 2 && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-800">
            {isFetching && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
              </div>
            )}
            {!isFetching && results.length === 0 && (
              <p className="py-3 text-center text-xs font-medium text-zinc-500">
                No intakes found
              </p>
            )}
            {!isFetching && results.length > 0 && (
              <div className="max-h-48 overflow-y-auto py-1">
                {results.map((intake) => (
                  <button
                    key={intake.id}
                    type="button"
                    onClick={() => {
                      onSelect(intake);
                      setSearch('');
                      setIsOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                        {intake.client_name}
                      </p>
                      <p className="text-xs text-zinc-500 truncate">
                        {intake.firm_name} &middot; {intake.status}
                      </p>
                    </div>
                    {intake.companion_count > 0 && (
                      <CompanionLabel
                        memberCount={intake.companion_count}
                        isPrimary={intake.is_companion_primary}
                        size="sm"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const LinkIntakesModal: React.FC<LinkIntakesModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [intakeA, setIntakeA] = useState<IntakeListItem | null>(null);
  const [intakeB, setIntakeB] = useState<IntakeListItem | null>(null);
  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [linkIntakes, { isLoading }] = useLinkIntakeCompanionsMutation();

  // Validate both intakes are from the same firm
  const firmMismatch =
    intakeA && intakeB && intakeA.firm_id !== intakeB.firm_id;

  // If either already has a pack, show it
  const intakeAHasPack = intakeA && intakeA.companion_count > 0;
  const intakeBHasPack = intakeB && intakeB.companion_count > 0;

  // Auto-select primary: if one is already primary in a pack, default to that
  const effectivePrimaryId = useMemo(() => {
    if (primaryId) return primaryId;
    if (intakeA?.is_companion_primary) return intakeA.id;
    if (intakeB?.is_companion_primary) return intakeB.id;
    return intakeA?.id || null;
  }, [primaryId, intakeA, intakeB]);

  const handleConfirm = useCallback(async () => {
    if (!intakeA || !intakeB || !effectivePrimaryId) return;

    try {
      await linkIntakes({
        intakeIdA: intakeA.id,
        intakeIdB: intakeB.id,
        primaryIntakeId: effectivePrimaryId,
      }).unwrap();

      // Reset and close
      setIntakeA(null);
      setIntakeB(null);
      setPrimaryId(null);
      onOpenChange(false);
    } catch {
      // Error handled by RTK Query
    }
  }, [intakeA, intakeB, effectivePrimaryId, linkIntakes, onOpenChange]);

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setIntakeA(null);
      setIntakeB(null);
      setPrimaryId(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md overflow-visible">
        <DialogHeader>
          <DialogTitle className="text-sm">Link Intakes</DialogTitle>
          <DialogDescription className="text-xs">
            Select two intakes to link into a companion group (pack).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <IntakePicker
            label="First Intake"
            selected={intakeA}
            onSelect={setIntakeA}
            onClear={() => {
              setIntakeA(null);
              setPrimaryId(null);
            }}
            excludeId={intakeB?.id}
          />

          <IntakePicker
            label="Second Intake"
            selected={intakeB}
            onSelect={setIntakeB}
            onClear={() => {
              setIntakeB(null);
              setPrimaryId(null);
            }}
            excludeId={intakeA?.id}
          />

          {/* Firm mismatch error */}
          {firmMismatch && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              These intakes belong to different firms ({intakeA.firm_name} and{' '}
              {intakeB.firm_name}). Only intakes from the same firm can be linked.
            </div>
          )}

          {/* Pack info warnings */}
          {!firmMismatch && (intakeAHasPack || intakeBHasPack) && intakeA && intakeB && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              {intakeAHasPack && intakeBHasPack ? (
                <p>
                  Both intakes already belong to companion groups. They will be
                  merged into a single group.
                </p>
              ) : (
                <p>
                  <strong>
                    {intakeAHasPack
                      ? intakeA?.client_name
                      : intakeB?.client_name}
                  </strong>{' '}
                  is already in a companion group ({intakeAHasPack ? intakeA?.companion_count : intakeB?.companion_count}{' '}
                  members). The other intake will be added to it.
                </p>
              )}
            </div>
          )}

          {/* Primary picker */}
          {intakeA && intakeB && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Crown className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Primary Intake
                </span>
              </div>
              <RadioGroup
                value={effectivePrimaryId || ''}
                onValueChange={setPrimaryId}
                className="space-y-1.5"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={intakeA.id} id="primary-a" />
                  <Label
                    htmlFor="primary-a"
                    className="text-xs font-medium text-zinc-900 dark:text-white cursor-pointer"
                  >
                    {intakeA.client_name}
                    {intakeA.is_companion_primary && (
                      <span className="ml-1 text-amber-600">(current primary)</span>
                    )}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={intakeB.id} id="primary-b" />
                  <Label
                    htmlFor="primary-b"
                    className="text-xs font-medium text-zinc-900 dark:text-white cursor-pointer"
                  >
                    {intakeB.client_name}
                    {intakeB.is_companion_primary && (
                      <span className="ml-1 text-amber-600">(current primary)</span>
                    )}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => handleClose(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            onClick={handleConfirm}
            disabled={!intakeA || !intakeB || !!firmMismatch || isLoading}
          >
            {isLoading ? 'Linking...' : 'Link Intakes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
